"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Mail, Phone, Send, Paperclip, Calendar, DollarSign, Building, User, FileText, Loader, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProjectInquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="200" id="start-your-consultation" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div ref={ref} className={`container mx-auto px-4 md:px-6 relative z-10 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {}
          <div className="space-y-8 pt-4">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight font-sans text-secondary">
                Secure Your <span className="text-primary">Operations</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md font-sans">
                Ready to protect your team and ensure regulatory compliance? Share your operational details, and let&apos;s develop a proactive safety strategy.
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-secondary/90 tracking-tight font-sans">Direct Line Scott Smith, Safety Consultant | Scott Smith Consulting, Cell: (714) 345-6144, Email: safetysmithca@yahoo.com</h3>
              <div className="flex flex-col items-start gap-3">
                <Button variant="ghost" className="h-auto py-3 px-4 text-base justify-start w-full sm:w-auto hover:bg-primary/10 hover:text-primary transition-colors group rounded-sm" asChild>
                  <Link to="mailto:safetysmithca@yahoo.com">
                    <Mail className="mr-3 size-5 text-primary group-hover:scale-110 transition-transform" />
                    safetysmithca@yahoo.com
                  </Link>
                </Button>
                <Button variant="ghost" className="h-auto py-3 px-4 text-base justify-start w-full sm:w-auto hover:bg-primary/10 hover:text-primary transition-colors group rounded-sm" asChild>
                  <Link to="tel:+17143456144">
                    <Phone className="mr-3 size-5 text-primary group-hover:scale-110 transition-transform" />
                    (714) 345-6144
                  </Link>
                </Button>
              </div>
            </div>

            <div className="p-6 rounded-sm border border-primary/20 bg-card shadow-sm border-l-4 border-l-primary">
              <h4 className="font-bold text-secondary mb-2 flex items-center gap-2 font-sans">
                <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
                Consultation Status
              </h4>
              <p className="text-sm text-muted-foreground font-sans">
                Currently scheduling site audits for next month. Typical response time for new inquiries is under 12 hours.
              </p>
            </div>
          </div>

          {}
          <div className="bg-card backdrop-blur-md border border-border rounded-sm p-6 md:p-8 shadow-md">
            <FormProvider formKey="consultation_form_lets_talk_safety" formId="consultation_form_lets_talk_safety" sectionName="project-inquiry" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            budget: "",
            timeline: "",
            project_details: "",
            attachment: undefined
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            project_details: {
              required: true,
              minLength: 20,
              maxLength: 2000
            }
          }} submitText="Request Consultation" successMessage="Consultation request received. Scott will review your details and contact you within 24 hours." errorMessage="Transmission failed. Please check your connection and try again.">
              <ProjectInquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ProjectInquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-sm bg-primary/10 flex items-center justify-center mb-4 shadow-sm">
          <Send className="size-10 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-secondary font-sans">Request Transmitted</h3>
        <p className="text-muted-foreground max-w-xs mx-auto font-sans">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-white rounded-sm transition-all">
          New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-bold text-secondary">Full Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input placeholder="John Smith" className="pl-9 rounded-sm border-input focus-visible:border-primary focus-visible:ring-primary/20 bg-background" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-bold text-secondary">Work Email</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="email" placeholder="jsmith@company.com" className="pl-9 rounded-sm border-input focus-visible:border-primary focus-visible:ring-primary/20 bg-background" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-bold text-secondary">Direct Phone <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="tel" placeholder="+1 (555) 000-0000" className="pl-9 rounded-sm border-input focus-visible:border-primary focus-visible:ring-primary/20 bg-background" {...field} />
                </div>
              </FormControl>
              <FormDescription className="text-xs">For urgent safety matters</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-bold text-secondary">Organization</FormLabel>
              <FormControl>
                <div className="relative">
                  <Building className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input placeholder="Industrial Solutions Inc." className="pl-9 rounded-sm border-input focus-visible:border-primary focus-visible:ring-primary/20 bg-background" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-bold text-secondary">Project Scope</FormLabel>
              <FormControl>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <select className="flex h-9 w-full rounded-sm border border-input bg-background px-3 py-1 pl-9 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50" {...field}>
                    <option value="" disabled>Select a level</option>
                    <option value="Single Audit">Single Site Audit</option>
                    <option value="Compliance Program">Full Compliance Program</option>
                    <option value="Training Workshop">Training Workshop</option>
                    <option value="Ongoing Retainer">Ongoing Safety Retainer</option>
                  </select>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="timeline" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-bold text-secondary">Target Date</FormLabel>
              <FormControl>
                <div className="relative">
                  <SafeHydrate><Calendar className="absolute left-3 top-2.5 size-4 text-muted-foreground" /></SafeHydrate>
                  <Input type="date" className="pl-9 rounded-sm border-input focus-visible:border-primary focus-visible:ring-primary/20 bg-background" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_details" rules={{
      required: "Operational details are required",
      minLength: {
        value: 20,
        message: "Please provide more context (min 20 chars)"
      },
      maxLength: {
        value: 2000,
        message: "Description is too long (max 2000 chars)"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-bold text-secondary">Operational Details</FormLabel>
            <FormControl>
              <div className="relative">
                <FileText className="absolute left-3 top-3 size-4 text-muted-foreground" />
                <Textarea placeholder="Describe your current safety challenges, facility type, and specific compliance goals." className="min-h-[120px] pl-9 rounded-sm border-input focus-visible:border-primary focus-visible:ring-primary/20 bg-background resize-y" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="attachment" render={({
      field: {
        value,
        onChange,
        ...fieldProps
      }
    }) => <FormItem>
            <FormLabel className="font-bold text-secondary">Relevant Documentation</FormLabel>
            <FormControl>
              <div className="relative">
                <Paperclip className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                <Input type="file" className="pl-9 pt-1.5 file:text-primary file:font-bold hover:file:text-primary/80 cursor-pointer rounded-sm border-input focus-visible:border-primary focus-visible:ring-primary/20 bg-background" onChange={e => {
            onChange(e.target.files ? e.target.files[0] : null);
          }} {...fieldProps} />
              </div>
            </FormControl>
            <FormDescription className="text-xs">Upload existing safety plans or audit reports (PDF preferred).</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-sm text-destructive text-sm flex items-center gap-2 font-sans">
          <span className="font-bold">Error:</span> {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-all duration-300 mt-4 rounded-sm uppercase tracking-wider">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : <>
            Request Consultation <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}